// File: frontend/src/hooks/useDfsPlayerPool.ts

import { useInfiniteQuery, InfiniteData, keepPreviousData } from '@tanstack/react-query';
import { fetchAvailablePlayers } from '@/api/dfs';
import { useAuth } from '@/contexts/AuthContext';
import { PaginatedAvailablePlayersResponse } from '@/api/schemas';
import { ApiError } from '@/api/auth';

const DEFAULT_PAGE_SIZE = 50;

// Define a type for the filters to make the query key more specific
interface DfsPlayerFilters {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  position: string;
  search: string;
}

// Define a more specific type for the query key array, allowing null for the identifier
type DfsPlayerPoolQueryKey = ['dfsPlayerPool', string | number | null, DfsPlayerFilters, number];

export function useDfsPlayerPool(
  contestIdOrAlias: string | number | null,
  filters: DfsPlayerFilters,
  pageSize: number = DEFAULT_PAGE_SIZE
) {
  const { token } = useAuth();

  return useInfiniteQuery<
    PaginatedAvailablePlayersResponse,
    ApiError,
    InfiniteData<PaginatedAvailablePlayersResponse>,
    DfsPlayerPoolQueryKey,
    number // Type of the pageParam (page number)
  >({
    // The queryKey uniquely identifies this query. When any part of it changes,
    // TanStack Query will automatically refetch the data from page 0.
    queryKey: ['dfsPlayerPool', contestIdOrAlias, filters, pageSize],

    // The function that fetches the data. It receives an object with a `pageParam`.
    queryFn: async ({ pageParam }) => {
      // The `enabled` check below ensures these are not null.
      const response = await fetchAvailablePlayers(contestIdOrAlias!, token, {
        skip: pageParam * pageSize,
        limit: pageSize,
        sort_by: filters.sortBy,
        sort_order: filters.sortOrder,
        position: filters.position === 'ALL' ? undefined : filters.position,
        search: filters.search || undefined,
      });
      return response;
    },

    // The initial value for `pageParam`. Our API uses `skip`, so we use page numbers (0, 1, 2...).
    initialPageParam: 0,

    // This function determines the `pageParam` for the *next* page.
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.has_more) {
        // The next page number is simply the count of pages we already have.
        // If we have 1 page (index 0), the next pageParam will be 1.
        // If we have 2 pages (indices 0, 1), the next pageParam will be 2.
        return allPages.length;
      }
      // If `has_more` is false, return undefined to signify there are no more pages.
      return undefined;
    },

    // The query will not run until we have a valid contest identifier.
    // The token check is implicitly handled by the API client function.
    enabled: !!contestIdOrAlias,

    // How long the data is considered fresh before becoming "stale". The pool
    // only changes on task cadence (salaries once per contest, projections on
    // the beat, is_starting ~1h before kickoff), so short windows just burn
    // requests; refreshes are background-only either way (keepPreviousData).
    staleTime: 1000 * 60 * 10, // 10 minutes

    // Keep the cache alive across page navigations: leaving the builder and
    // coming back within this window serves the pool instantly (a stale entry
    // still renders immediately and refreshes in the background).
    gcTime: 1000 * 60 * 15, // 15 minutes

    // Keep previous data visible while a new search/filter query loads in the background.
    // Prevents the full-page loading skeleton from triggering on search/filter changes.
    placeholderData: keepPreviousData,
  });
}
